import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listUsers, AdminUserItem } from "@/services/adminApi";
import { resolveTierBadge } from "@/lib/tierBadge";
import { roleBadgeLabel } from "@/lib/roleBadge";
import { cn } from "@/lib/utils";
import { UserPlus } from "lucide-react";

function formatDate(dateStr: string) {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

export function RecentUsersCard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await listUsers(navigate);
        setUsers(res.items);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const recentUsers = useMemo(() => {
    return [...users]
      .sort((a, b) => {
        const da = a.registration_date ? new Date(a.registration_date).getTime() : 0;
        const db = b.registration_date ? new Date(b.registration_date).getTime() : 0;
        return db - da;
      })
      .slice(0, 10);
  }, [users]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Derniers inscrits</CardTitle>
        <UserPlus className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Chargement...</div>
        ) : error ? (
          <div className="text-sm text-red-600">Erreur</div>
        ) : recentUsers.length === 0 ? (
          <div className="text-sm text-muted-foreground">Aucun utilisateur</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Abonnement</TableHead>
                <TableHead>Inscription</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentUsers.map((u) => (
                <TableRow key={u.user_id}>
                  <TableCell className="font-medium">
                    {[u.profile?.first_name, u.profile?.last_name]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {u.profile?.email || "—"}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const badge = resolveTierBadge({
                        is_business: u.organization?.is_business,
                        organization_tier: u.organization?.tier,
                        subscription_type: u.subscription_type,
                      });
                      return (
                        <div className="flex flex-col items-start gap-0.5">
                          <Badge
                            variant={badge?.variant ?? "outline"}
                            className={cn("uppercase", badge?.className)}
                          >
                            {badge?.label ?? "none"}
                          </Badge>
                          {u.organization?.member_role && (
                            <span className="text-xs text-muted-foreground">
                              {roleBadgeLabel(u.organization.member_role)}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {u.registration_date ? formatDate(u.registration_date) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
